import dayjs, { Dayjs } from "dayjs";

export type IChartRange = [Dayjs, Dayjs];
export const defaultAnalyticsSpan = 7;
export const defaultLogicalFormat = 'MM DD YYYY';
export const defaultDisplayFormat = 'MMM DD';

export interface IGraphData {
  date: Dayjs;
  views: number;
}

export interface IChartData {
  label: string;
  data: Array<IGraphData>;
}

/**
 * GET /brands/:id/s3file/:fileId/analytics — totals and one row per day,
 * counted in SQL (TDC-1676). Days are `YYYY-MM-DD` in the `tz` we sent.
 */
export interface IAnalyticsSummary {
  file_id: number;
  brand_id: number;
  tz: string;
  from: string;
  to: string;
  earliest: string | null;
  earliest_day: string | null;
  filters: { project: number | null; source: string | null };
  totals: {
    impressions: number;
    plays: number;
    watch_seconds: number;
    avg_play_seconds: number;
  };
  daily: Array<{ date: string; plays: number; watch_seconds: number; impressions: number }>;
}

// --------------- METHODS -------------------

const defaultAnalyticsRange = (startDate?: string): IChartRange => {
  const startDay = startDate ? dayjs(startDate) : dayjs().subtract(defaultAnalyticsSpan - 1, 'days');
  return [startDay.startOf('day'), dayjs()]
};

const initializeEmptyRange = (range: IChartRange): Array<IGraphData> => {
  const [ startTime, endTime ] = range;
  const totalSpan = endTime.diff(startTime, 'days') + 1;

  if (totalSpan < 0) return [];
  return new Array(totalSpan).fill(0).map((_, i) => ({ views: 0, date: startTime.add(i, 'days') }));
}

export const formatDuration = (value: number) => {
  const hour = value / 3600;
  if (hour >= 1) return { value: hour.toFixed(2), unit: 'hour'};

  const min = value / 60;
  if (min >= 1) return { value: min.toFixed(2), unit: 'min'};

  return { value: value.toFixed(2), unit: 'sec'};
}

const initialChartState = (): Array<IChartData> => [{
  label: 'Seconds',
  data: initializeEmptyRange(defaultAnalyticsRange())
}];

/** The zone the request buckets days in — the viewer's. */
export const viewerTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/** A range bound as the API's `from` / `to` query value. */
export const dayParam = (d: Dayjs): string => d.format('YYYY-MM-DD');

/** Seconds watched per day — the series the graph has always drawn. */
const chartDataFromSummary = (summary: IAnalyticsSummary): Array<IChartData> => [{
  label: 'Seconds',
  data: (summary.daily || []).map((d) => ({ date: dayjs(d.date).startOf('day'), views: d.watch_seconds })),
}];

export {
  defaultAnalyticsRange,
  initialChartState,
  chartDataFromSummary,
}
